import React from 'react';

export default function TermsAndConditions() {
  return (
    <main className="min-h-screen bg-neutral-50 text-neutral-900 font-sans">
      {/* Hero / Banner */}
      <section className="relative overflow-hidden">
        {/* Gradient backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-[#1b0d0b] to-neutral-700 opacity-95" />
        {/* Subtle dot pattern overlay */}
        <svg aria-hidden="true" className="absolute -top-12 -left-12 h-[38rem] w-[38rem] opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="dots" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
        <div className="relative mx-auto w-full max-w-5xl px-4 py-16 md:py-24 text-center text-white">
          <div className="mx-auto inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-1 backdrop-blur">
            <span className="text-[11px] tracking-[0.25em]">LEGAL</span>
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight">Terms &amp; Conditions</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-white/80">
            Please read these terms carefully before using our services. By continuing, you agree to be bound by them.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:py-14">
        {/* Quick summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SummaryCard
            title="Privacy First"
            text="We handle your data with care and transparency."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.25c-3.728 0-6.75 3.022-6.75 6.75v2.086a4.5 4.5 0 0 0-1.5 3.364v3.75A3.75 3.75 0 0 0 7.5 21.75h9a3.75 3.75 0 0 0 3.75-3.75v-3.75a4.5 4.5 0 0 0-1.5-3.364V9c0-3.728-3.022-6.75-6.75-6.75Zm0 1.5A5.25 5.25 0 0 1 17.25 9v1.5h-10.5V9A5.25 5.25 0 0 1 12 3.75Zm-6 9.75h12c.621 0 1.125.504 1.125 1.125v3.125A2.625 2.625 0 0 1 16.5 20.375h-9A2.625 2.625 0 0 1 4.875 17.75v-3.125c0-.621.504-1.125 1.125-1.125Z"/></svg>
            )}
          />
          <SummaryCard
            title="Clear Eligibility"
            text="You must be 12+ and compliant with local laws."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M9 2.25a.75.75 0 0 0-.75.75V6H6A2.25 2.25 0 0 0 3.75 8.25v9A2.25 2.25 0 0 0 6 19.5h12A2.25 2.25 0 0 0 20.25 17.25v-9A2.25 2.25 0 0 0 18 6h-2.25V3a.75.75 0 0 0-1.5 0v3h-3V3A.75.75 0 0 0 9 2.25Zm-3 6A.75.75 0 0 1 6.75 7.5h10.5a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 8.25Z"/></svg>
            )}
          />
          <SummaryCard
            title="Service Availability"
            text="We may update or suspend features when necessary."
            icon={(
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9Zm.75 4.5a.75.75 0 0 0-1.5 0v5.25c0 .414.336.75.75.75h3.75a.75.75 0 0 0 0-1.5H12.75Z"/></svg>
            )}
          />
        </div>

        {/* Detailed sections */}
        <div className="mt-10 space-y-8">
          <DetailSection
            number="1"
            title="Introduction"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M4.5 6.75A2.25 2.25 0 0 1 6.75 4.5h10.5A2.25 2.25 0 0 1 19.5 6.75v10.5A2.25 2.25 0 0 1 17.25 19.5H6.75A2.25 2.25 0 0 1 4.5 17.25V6.75Zm3 2.25h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1 0-1.5Zm0 3h9a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1 0-1.5Z"/></svg>
            )}
          >
            By accessing or using this website, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.
          </DetailSection>

          <DetailSection
            number="2"
            title="Eligibility"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.25a9.75 9.75 0 1 0 9.75 9.75A9.761 9.761 0 0 0 12 2.25Zm0 4.5a2.25 2.25 0 1 1-2.25 2.25A2.25 2.25 0 0 1 12 6.75Zm0 11.25a7.5 7.5 0 0 1-6-3.03 6 6 0 0 1 12 0 7.5 7.5 0 0 1-6 3.03Z"/></svg>
            )}
          >
            You must be at least 18 years old to use our services. By using this site, you represent and warrant that you meet this requirement.
          </DetailSection>

          <DetailSection
            number="3"
            title="Services"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6.75A2.25 2.25 0 0 1 5.25 4.5h13.5A2.25 2.25 0 0 1 21 6.75v10.5A2.25 2.25 0 0 1 18.75 19.5H5.25A2.25 2.25 0 0 1 3 17.25V6.75Zm3 1.5a.75.75 0 0 0 0 1.5h12a.75.75 0 0 0 0-1.5H6Z"/></svg>
            )}
          >
            We provide a secure platform for booking and managing appointments, as well as related services. We reserve the right to modify, suspend, or discontinue any part of our services without notice.
          </DetailSection>

          <DetailSection
            number="4"
            title="User Responsibilities"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.25a4.5 4.5 0 0 1 4.5 4.5V9h1.5A2.25 2.25 0 0 1 20.25 11.25v7.5A2.25 2.25 0 0 1 18 21H6a2.25 2.25 0 0 1-2.25-2.25v-7.5A2.25 2.25 0 0 1 5.25 9H6V6.75A4.5 4.5 0 0 1 10.5 2.25h3ZM7.5 9h9V6.75a3 3 0 1 0-6 0V9Z"/></svg>
            )}
          >
            Provide accurate, current, and complete information when using our forms; maintain the confidentiality of your personal information; and comply with all applicable laws and regulations.
          </DetailSection>

          <DetailSection
            number="5"
            title="Privacy"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.25a9.75 9.75 0 0 0-9.75 9.75 9.75 9.75 0 0 0 9.75 9.75 9.75 9.75 0 0 0 9.75-9.75A9.75 9.75 0 0 0 12 2.25Zm0 4.5A3 3 0 1 1 9 9.75 3 3 0 0 1 12 6.75Z"/></svg>
            )}
          >
            We respect your privacy and are committed to protecting your personal information. Please review our Privacy Policy to understand how we handle your data.
          </DetailSection>

          <DetailSection
            number="6"
            title="Limitation of Liability"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.25a9.75 9.75 0 1 0 9.75 9.75A9.761 9.761 0 0 0 12 2.25Zm-.75 4.5a.75.75 0 0 1 1.5 0v6a.75.75 0 0 1-1.5 0v-6Zm.75 9a1.125 1.125 0 1 0 1.125 1.125A1.125 1.125 0 0 0 12 15.75Z"/></svg>
            )}
          >
            To the fullest extent permitted by law, we disclaim all liability for any damages resulting from your use of our website or services. We do not guarantee uninterrupted or error-free operation.
          </DetailSection>

          <DetailSection
            number="7"
            title="Changes to Terms"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M6.75 3a.75.75 0 0 0-.75.75V6h11.25V3.75a.75.75 0 0 0-1.5 0V4.5h-7.5V3.75a.75.75 0 0 0-.75-.75ZM4.5 7.5h15a.75.75 0 0 1 .75.75v9A2.25 2.25 0 0 1 18 19.5H6a2.25 2.25 0 0 1-2.25-2.25v-9a.75.75 0 0 1 .75-.75Z"/></svg>
            )}
          >
            We may update these Terms & Conditions from time to time. Updated versions will be posted on this page with a revised “Last Updated” date.
          </DetailSection>

          <DetailSection
            number="8"
            title="Governing Law"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M4.5 4.5h15v6a.75.75 0 0 1-.75.75h-7.5v7.5a.75.75 0 0 1-1.5 0v-7.5H5.25A.75.75 0 0 1 4.5 10.5v-6Z"/></svg>
            )}
          >
            These Terms & Conditions shall be governed by and construed in accordance with the laws of your jurisdiction, without regard to its conflict of law provisions.
          </DetailSection>

          <DetailSection
            number="9"
            title="Contact Us"
            icon={(
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M2.25 6.75A2.25 2.25 0 0 1 4.5 4.5h15a2.25 2.25 0 0 1 2.25 2.25v10.5A2.25 2.25 0 0 1 19.5 19.5H4.5A2.25 2.25 0 0 1 2.25 17.25V6.75Zm2.4.6a.75.75 0 0 0-.6 1.38l7.2 3.9a.75.75 0 0 0 .72 0l7.2-3.9a.75.75 0 0 0-.6-1.38L12 10.32 4.65 7.35Z"/></svg>
            )}
          >
            If you have any questions about these Terms & Conditions, please contact us at
            {' '}<a href="mailto:emefabansa@gmail.com" className="text-neutral-900 underline decoration-1 underline-offset-2 hover:no-underline">emefabansa@gmail.com</a>.
          </DetailSection>
        </div>

        <footer className="mt-14 text-center text-xs text-neutral-500">
          Last Updated: September 2025
        </footer>
      </div>
    </main>
  );
}

function SummaryCard({ title, text, icon }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900 text-white">
          {icon}
        </div>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <p className="mt-2 text-[13px] text-neutral-600">{text}</p>
    </div>
  );
}

function DetailSection({ number, title, icon, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-900 text-white text-xs font-semibold">
          {number}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="text-neutral-900">{icon}</div>
            <h2 className="text-base font-semibold text-neutral-900">{title}</h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-neutral-700">{children}</p>
        </div>
      </div>
    </section>
  );
}